import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import { Link } from "react-router-dom";
import "leaflet/dist/leaflet.css";

const mk = (color, letter) => L.divIcon({
  className: "",
  html: `<div style="width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center"><span style="transform:rotate(45deg);color:#fff;font-size:11px;font-weight:700">${letter}</span></div>`,
  iconSize: [28, 28], iconAnchor: [14, 28], popupAnchor: [0, -26],
});
const JOB_ICON = mk("#f97316", "V");
const FREELA_ICON = mk("#2563eb", "F");
const ME_ICON = L.divIcon({ className: "", html: '<div style="width:16px;height:16px;border-radius:50%;background:#10b981;border:3px solid #fff;box-shadow:0 0 0 4px rgba(16,185,129,.3)"></div>', iconSize: [16, 16], iconAnchor: [8, 8] });

function FitBounds({ points, center, radiusKm }) {
  const map = useMap();
  useEffect(() => {
    if (center && radiusKm) {
      const c = L.latLng(center.lat, center.lng);
      map.fitBounds(c.toBounds(radiusKm * 2000), { padding: [20, 20] });
    } else if (points.length) {
      map.fitBounds(L.latLngBounds(points.map(p => [p.lat, p.lng])), { padding: [30, 30], maxZoom: 14 });
    }
  }, [map, points.length, center?.lat, center?.lng, radiusKm]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function WorkResultsMap({ jobs = [], freelancers = [], center, radiusKm, onJobSelect }) {
  const jobPts = jobs.filter(j => j.lat != null && j.lng != null);
  const frPts = freelancers.filter(f => f.lat != null && f.lng != null);
  const all = [...jobPts, ...frPts, ...(center ? [center] : [])];
  const initial = center || all[0] || { lat: -23.55, lng: -46.63 };
  const hidden = (jobs.length - jobPts.length) + (freelancers.length - frPts.length);

  return (
    <div className="space-y-1" data-testid="work-results-map">
      <div className="h-[380px] rounded-xl overflow-hidden border">
        <MapContainer center={[initial.lat, initial.lng]} zoom={12} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
          <FitBounds points={all} center={center} radiusKm={radiusKm} />
          {center && (<>
            <Marker position={[center.lat, center.lng]} icon={ME_ICON}><Popup>Você está aqui</Popup></Marker>
            {radiusKm && <Circle center={[center.lat, center.lng]} radius={radiusKm * 1000} pathOptions={{ color: "#10b981", fillColor: "#10b981", fillOpacity: 0.08, weight: 2, dashArray: "6 6" }} />}
          </>)}
          {jobPts.map(j => (
            <Marker key={j.job_id} position={[j.lat, j.lng]} icon={JOB_ICON}>
              <Popup>
                <div className="text-sm min-w-[160px]" data-testid={`map-job-${j.job_id}`}>
                  <div className="font-bold">{j.title}</div>
                  <div className="text-xs text-slate-600">{j.category} · R$ {Number(j.budget || 0).toFixed(2)}{j.distance_km != null ? ` · ${j.distance_km} km` : ""}</div>
                  <button type="button" className="text-xs text-blue-600 underline mt-1" onClick={() => onJobSelect?.(j)}>Ver vaga</button>
                </div>
              </Popup>
            </Marker>
          ))}
          {frPts.map(f => (
            <Marker key={f.user_id} position={[f.lat, f.lng]} icon={FREELA_ICON}>
              <Popup>
                <div className="text-sm min-w-[160px]" data-testid={`map-freelancer-${f.user_id}`}>
                  <div className="font-bold">{f.name}</div>
                  <div className="text-xs text-slate-600">{f.headline || (f.skills || []).slice(0, 3).join(", ")}{f.distance_km != null ? ` · ${f.distance_km} km` : ""}</div>
                  <Link to={`/user/${f.user_id}`} className="text-xs text-blue-600 underline mt-1 inline-block">Ver perfil</Link>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      <div className="text-[11px] text-muted-foreground flex flex-wrap gap-3 px-1">
        <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-orange-500 mr-1" />Vagas ({jobPts.length})</span>
        <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-600 mr-1" />Freelancers ({frPts.length})</span>
        {center && <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 mr-1" />Você{radiusKm ? ` · raio ${radiusKm} km` : ""}</span>}
        {hidden > 0 && <span data-testid="map-hidden-count">{hidden} resultado(s) sem localização no mapa</span>}
      </div>
    </div>
  );
}
